// Shared deal summary on the RFQ page: who it needs, what it waits on, the
// latest messages, suppliers. Records only (Deal State); anything derived by a
// rule is labelled so. A person can set the handoff directly here, with no AI
// involved, or ask Copilot.

import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowRightLeft, ChevronDown, Sparkles } from 'lucide-react';
import type { DealState } from '@shared/copilot/deal-state';
import { ATTENTION_LABEL, ATTENTION_STATES, type AttentionState } from '@shared/copilot/attention';
import type { ApiError } from '@/lib/api';
import { getDealState, getTeamMembers, listDrafts, recordHandoff, type DraftView } from '@/lib/copilot-api';
import { SavedDraft } from './proposal-cards';
import { useCopilot } from '@/lib/copilot-context';

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '');
const money = (c: number, cur: string) => `${(c / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;

export function bestQuote(s: DealState) {
  const qs = s.suppliers.filter((x) => x.quote).map((x) => ({ name: x.name, ...x.quote! }));
  if (!qs.length) return null;
  // Only compare like with like: the lowest price in the most common currency.
  const cur = qs.map((q) => q.currency).sort((a, b) => qs.filter((q) => q.currency === b).length - qs.filter((q) => q.currency === a).length)[0];
  return qs.filter((q) => q.currency === cur).sort((a, b) => a.unitPriceCents - b.unitPriceCents)[0]!;
}

export function DealSummaryView({ s, onAsk, onUpdate }: { s: DealState; onAsk: (text: string) => void; onUpdate: () => void }) {
  const [showSummary, setShowSummary] = useState(false);
  const h = s.handoff && s.handoff.attention !== 'done' ? s.handoff : null;
  const quoted = s.suppliers.filter((x) => x.quote).length;
  const best = bestQuote(s);
  return (
    <div className="mt-4 rounded-lg border border-border bg-surface p-3 text-sm space-y-2.5 min-w-0">
      {h ? (
        <div className="rounded-md bg-primary-muted/60 border border-primary/20 p-2.5 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="pill pill-primary font-semibold">{h.label}</span>
            <span className="text-xs text-foreground-muted">{h.fromName ? `from ${h.fromName}, ` : ''}{when(h.at)}</span>
          </div>
          {h.note && <div className="text-sm whitespace-pre-wrap break-words">{h.note}</div>}
          {h.newerCustomerEmail && <div className="flex items-center gap-1 text-xs font-medium text-amber-800 dark:text-amber-300"><AlertTriangle className="w-3.5 h-3.5" />The customer has written since this handoff.</div>}
          {h.summary && (
            <div>
              <button className="text-xs text-primary inline-flex items-center gap-0.5" onClick={() => setShowSummary((v) => !v)} aria-expanded={showSummary}>
                {h.summaryByCopilot ? 'Summary drafted by APD Copilot' : 'Summary'}<ChevronDown className={`w-3 h-3 ${showSummary ? 'rotate-180' : ''}`} />
              </button>
              {showSummary && (
                <div className="mt-1 text-xs space-y-1">
                  {h.summary.currentBestOffer && <div><span className="font-semibold">Current best:</span> {h.summary.currentBestOffer}</div>}
                  {h.summary.doNotPromise?.length ? <div><span className="font-semibold">Do not promise:</span> {h.summary.doNotPromise.join('; ')}</div> : null}
                  {h.summary.nextActions?.length ? <div><span className="font-semibold">Next:</span> {h.summary.nextActions.join('; ')}</div> : null}
                  {h.summary.escalateIf?.length ? <div><span className="font-semibold">Escalate if:</span> {h.summary.escalateIf.join('; ')}</div> : null}
                  {h.summary.outstanding?.length ? <div><span className="font-semibold">Outstanding:</span> {h.summary.outstanding.join('; ')}</div> : null}
                </div>
              )}
            </div>
          )}
        </div>
      ) : null}

      <div className="flex flex-wrap gap-1.5">
        {s.waitingOn.filter((w) => w.basis !== 'handoff').map((w, i) => (
          <span key={i} className="pill pill-warning" title={w.derived ? 'Derived from the latest records by a rule; not set by a person' : undefined}>{w.label}</span>
        ))}
      </div>

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
        <div className="min-w-0"><dt className="text-foreground-muted inline">Customer last wrote: </dt><dd className="inline break-words">{s.customer.lastFromCustomer ? `${when(s.customer.lastFromCustomer.at)} · ${s.customer.lastFromCustomer.subject ?? ''}` : 'nothing on file'}</dd></div>
        <div className="min-w-0"><dt className="text-foreground-muted inline">We last wrote: </dt><dd className="inline break-words">{s.customer.lastFromUs ? `${when(s.customer.lastFromUs.at)}${s.customer.lastFromUs.staffName ? ` · ${s.customer.lastFromUs.staffName}` : ''}` : 'nothing on file'}</dd></div>
        <div className="min-w-0"><dt className="text-foreground-muted inline">Suppliers: </dt><dd className="inline">{s.suppliers.length} contacted or quoting · {quoted} quoted</dd></div>
        <div className="min-w-0"><dt className="text-foreground-muted inline">Lowest quote: </dt><dd className="inline">{best ? `${money(best.unitPriceCents, best.currency)}${best.condition ? ` ${best.condition}` : ''} · ${best.name}` : 'none yet'}</dd></div>
      </dl>

      <div className="flex flex-wrap gap-2">
        <button className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-md bg-primary text-primary-foreground" onClick={() => onAsk('Where are we at?')}><Sparkles className="w-3.5 h-3.5" />Where are we at?</button>
        <button className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md border border-border hover:bg-surface-muted" onClick={onUpdate}><ArrowRightLeft className="w-3.5 h-3.5" />{h ? 'Update handoff' : 'Hand off / set status'}</button>
      </div>
    </div>
  );
}

function HandoffForm({ rfqPublicId, current, onDone }: { rfqPublicId: string; current: string | null; onDone: () => void }) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const team = useQuery({ queryKey: ['team', 'members'], queryFn: () => getTeamMembers(() => getToken()), staleTime: 5 * 60_000 });
  const [attention, setAttention] = useState<AttentionState>('needs_user');
  const [assignee, setAssignee] = useState('');
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () => recordHandoff(rfqPublicId, { input: { attention, assigneeMembershipId: attention === 'needs_user' ? assignee : null, note: note.trim() || undefined }, expectedCurrent: current }, () => getToken()),
    onSuccess: () => { for (const k of [['copilot', 'deal-state', rfqPublicId], ['copilot', 'handoffs', rfqPublicId], ['copilot', 'attention']]) void qc.invalidateQueries({ queryKey: k }); onDone(); },
  });
  const field = 'w-full min-w-0 text-sm border border-border rounded-md px-2 py-1.5 bg-background';
  return (
    <div className="mt-2 rounded-lg border border-border p-3 space-y-2 text-sm">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <select className={field} aria-label="State" value={attention} onChange={(e) => setAttention(e.target.value as AttentionState)}>
          {ATTENTION_STATES.map((a) => <option key={a} value={a}>{a === 'needs_user' ? 'Needs a person' : ATTENTION_LABEL[a]}</option>)}
        </select>
        {attention === 'needs_user' && (
          <select className={field} aria-label="Who" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            <option value="">Choose…</option>
            {(team.data?.members ?? []).filter((m) => m.role !== 'viewer').map((m) => <option key={m.membershipId} value={m.membershipId}>{m.fullName ?? m.email}{m.isSelf ? ' (you)' : ''}</option>)}
          </select>
        )}
      </div>
      <textarea className={`${field} min-h-[64px]`} aria-label="Note" placeholder="What should they do? What must not be promised?" value={note} onChange={(e) => setNote(e.target.value)} maxLength={4000} />
      {save.isError && <div className="text-xs text-destructive">{(save.error as unknown as ApiError)?.message}</div>}
      <div className="flex gap-2">
        <button className="px-2.5 py-1.5 text-xs font-semibold rounded-md bg-primary text-primary-foreground disabled:opacity-50" disabled={save.isPending || (attention === 'needs_user' && !assignee)} onClick={() => save.mutate()}>Save</button>
        <button className="px-2.5 py-1.5 text-xs rounded-md border border-border" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}

export function DealSummaryCard({ rfqPublicId }: { rfqPublicId: string }) {
  const { getToken } = useAuth();
  const { capability, ask } = useCopilot();
  const [editing, setEditing] = useState(false);
  const { data } = useQuery({
    queryKey: ['copilot', 'deal-state', rfqPublicId],
    queryFn: () => getDealState(rfqPublicId, () => getToken()),
    enabled: capability.canUse,
    staleTime: 30_000,
    retry: false,
  });
  if (!capability.canUse || !data) return null;
  return (
    <>
      <DealSummaryView s={data} onAsk={(text) => ask({ scope: { type: 'rfq', ref: rfqPublicId }, text, autoSend: true })} onUpdate={() => setEditing((v) => !v)} />
      {editing && <HandoffForm rfqPublicId={rfqPublicId} current={data.handoff?.id ?? null} onDone={() => setEditing(false)} />}
      <SavedDrafts rfqPublicId={rfqPublicId} />
    </>
  );
}

/** Drafts saved on this RFQ from Copilot, reviewable by either coworker. */
function SavedDrafts({ rfqPublicId }: { rfqPublicId: string }) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({ queryKey: ['copilot', 'drafts', rfqPublicId], queryFn: () => listDrafts(rfqPublicId, () => getToken()), staleTime: 30_000 });
  const drafts = data?.drafts ?? [];
  if (!drafts.length) return null;
  const pending = drafts.filter((d) => d.status === 'draft').length;
  const update = (d: DraftView) => qc.setQueryData(['copilot', 'drafts', rfqPublicId], { drafts: drafts.map((x) => (x.id === d.id ? d : x)) });
  return (
    <div className="mt-2 text-sm">
      <button className="text-xs text-primary inline-flex items-center gap-0.5" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        {drafts.length} saved email draft{drafts.length === 1 ? '' : 's'}{pending ? ` · ${pending} not yet in Gmail` : ''}<ChevronDown className={`w-3 h-3 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="mt-2 space-y-2">{drafts.map((d) => <SavedDraft key={d.id} draft={d} onChange={update} />)}</div>}
    </div>
  );
}
